import { Link } from "react-router-dom";
import { LegalTopBar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Reveal } from "@/components/Reveal";

const CONTACT = "support@buildmakewin.com";

const PendingBanner = () => (
  <div
    data-testid="pending-copy-banner"
    className="mb-10 rounded-xl border border-growth/30 bg-sage/60 px-5 py-4 font-body text-sm leading-relaxed text-evergreen"
  >
    The final owner-approved copy for this page is being prepared. The outline below shows the
    structure this page will follow. Questions in the meantime:{" "}
    <a href={`mailto:${CONTACT}`} className="font-semibold underline underline-offset-4">
      {CONTACT}
    </a>
  </div>
);

const Shell = ({ eyebrow, title, intro, children, testId }) => (
  <main className="bg-cream" data-testid={testId}>
    <LegalTopBar />
    <div className="grain relative overflow-hidden bg-evergreen text-white">
      <div className="mx-auto max-w-[860px] px-4 py-16 sm:px-6 lg:py-20">
        <p className="font-display text-xs font-semibold uppercase tracking-[0.22em] text-leaf">
          {eyebrow}
        </p>
        <h1 className="mt-3 font-display text-4xl font-semibold tracking-tight sm:text-5xl">
          {title}
        </h1>
        {intro && <p className="mt-5 max-w-[620px] font-body text-base leading-relaxed text-sage">{intro}</p>}
      </div>
    </div>
    <div className="mx-auto max-w-[860px] px-4 py-16 sm:px-6 lg:py-20">
      <Reveal>{children}</Reveal>
    </div>
    <Footer />
  </main>
);

const H2 = ({ children }) => (
  <h2 className="mt-12 font-display text-2xl font-semibold tracking-tight text-ink first:mt-0">
    {children}
  </h2>
);

const P = ({ children }) => (
  <p className="mt-4 font-body text-base leading-relaxed text-slate">{children}</p>
);

export const CoachEROverviewPage = () => (
  <Shell
    testId="coacher-overview-page"
    eyebrow="Product"
    title="CoachER"
    intro="Your Digital Dugout Assistant — the first product owned and developed by Build Make Win Ventures LLC."
  >
    <H2>What CoachER does</H2>
    <P>
      CoachER helps youth sports coaches organize their teams, build better practices, manage
      lineups, capture player notes, and spend less time piecing everything together across
      different tools.
    </P>
    <P>
      It was built from firsthand experience in the dugout — managing practice plans, player
      notes, schedules, lineups, and communication across too many disconnected tools.
    </P>
    <div className="mt-10 flex flex-wrap gap-4">
      <a
        href="https://trycoacher.com"
        target="_blank"
        rel="noopener noreferrer"
        data-testid="coacher-overview-visit-btn"
        className="rounded-xl bg-growth px-7 py-3.5 font-display text-base font-semibold text-white transition-colors duration-200 hover:bg-evergreen"
      >
        Visit CoachER
      </a>
      <Link
        to="/coacher/support"
        data-testid="coacher-overview-support-link"
        className="rounded-xl border border-evergreen/40 px-7 py-3.5 font-display text-base font-semibold text-evergreen transition-colors duration-200 hover:bg-evergreen hover:text-white"
      >
        Get support
      </Link>
    </div>
    <div className="mt-12 grid gap-6 sm:grid-cols-2">
      <img
        src="/assets/coacher-lineup.jpeg"
        alt="CoachER game lineup screen with field positions and bench"
        data-testid="coacher-screenshot-lineup"
        className="rounded-[20px] border border-ink/10 shadow-[0_20px_60px_rgba(23,33,29,0.12)]"
        loading="lazy"
      />
      <img
        src="/assets/coacher-batting.jpeg"
        alt="CoachER AI-optimized batting order built from coaching notes"
        data-testid="coacher-screenshot-batting"
        className="rounded-[20px] border border-ink/10 shadow-[0_20px_60px_rgba(23,33,29,0.12)]"
        loading="lazy"
      />
    </div>
    <div className="mt-14 grid gap-4 sm:grid-cols-2">
      {[
        { label: "Privacy Policy", to: "/coacher/privacy", testId: "coacher-overview-privacy-link" },
        { label: "Terms of Use", to: "/coacher/terms", testId: "coacher-overview-terms-link" },
        { label: "Support", to: "/coacher/support", testId: "coacher-overview-support-card" },
        { label: "Delete your account", to: "/coacher/delete-account", testId: "coacher-overview-delete-link" },
      ].map((c) => (
        <Link
          key={c.to}
          to={c.to}
          data-testid={c.testId}
          className="group flex items-center justify-between rounded-[20px] border border-ink/10 bg-white px-6 py-5 transition-shadow duration-300 hover:shadow-[0_8px_30px_rgba(23,33,29,0.12)]"
        >
          <span className="font-display text-base font-semibold text-ink">{c.label}</span>
          <svg viewBox="0 0 24 24" className="h-4 w-4 text-growth transition-transform duration-200 group-hover:translate-x-1" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M5 12h14M13 6l6 6-6 6" />
          </svg>
        </Link>
      ))}
    </div>
  </Shell>
);

export const PrivacyPage = () => (
  <Shell
    testId="privacy-page"
    eyebrow="CoachER legal"
    title="Privacy Policy"
    intro="How CoachER and Build Make Win Ventures LLC collect, use, and protect information."
  >
    <PendingBanner />
    <H2>Information we collect</H2>
    <P>Owner-approved copy pending. This section will describe account, team, and device information collected by CoachER.</P>
    <H2>How we use information</H2>
    <P>Owner-approved copy pending. This section will describe the purposes for which information is used.</P>
    <H2>Youth player information</H2>
    <P>Owner-approved copy pending. This section will describe how limited information about youth players, entered by adult coaches, is handled.</P>
    <H2>Sharing and third-party services</H2>
    <P>Owner-approved copy pending. This section will list the service providers that process data on our behalf.</P>
    <H2>Data retention and deletion</H2>
    <P>
      Owner-approved copy pending. To request deletion of your account and associated data, see{" "}
      <Link to="/coacher/delete-account" data-testid="privacy-delete-link" className="font-semibold text-growth underline underline-offset-4">
        Delete your account
      </Link>
      .
    </P>
    <H2>Contact</H2>
    <P>
      Privacy questions:{" "}
      <a href={`mailto:${CONTACT}`} data-testid="privacy-contact-email" className="font-semibold text-growth underline underline-offset-4">
        {CONTACT}
      </a>
    </P>
  </Shell>
);

export const TermsPage = () => (
  <Shell
    testId="terms-page"
    eyebrow="CoachER legal"
    title="Terms of Use"
    intro="The terms that govern use of CoachER, a product of Build Make Win Ventures LLC."
  >
    <PendingBanner />
    <H2>Agreement to terms</H2>
    <P>Owner-approved copy pending. This section will describe the agreement between you and Build Make Win Ventures LLC.</P>
    <H2>Accounts and eligibility</H2>
    <P>Owner-approved copy pending. This section will describe account requirements, including that accounts are for adults age 18 or older.</P>
    <H2>Subscriptions and billing</H2>
    <P>Owner-approved copy pending. This section will describe subscription terms, renewal, and cancellation.</P>
    <H2>Acceptable use</H2>
    <P>Owner-approved copy pending. This section will describe permitted and prohibited uses of CoachER.</P>
    <H2>Contact</H2>
    <P>
      Questions about these terms:{" "}
      <a href={`mailto:${CONTACT}`} data-testid="terms-contact-email" className="font-semibold text-growth underline underline-offset-4">
        {CONTACT}
      </a>
    </P>
  </Shell>
);

export const SupportPage = () => (
  <Shell
    testId="support-page"
    eyebrow="CoachER help"
    title="Support"
    intro="We're a small, founder-led company. You will hear back from a real person."
  >
    <H2>Contact support</H2>
    <P>
      Email us at{" "}
      <a href={`mailto:${CONTACT}`} data-testid="support-contact-email" className="font-semibold text-growth underline underline-offset-4">
        {CONTACT}
      </a>{" "}
      and include the email address on your CoachER account, the device you're using, and a short
      description of what happened. Screenshots help.
    </P>
    <H2>Common requests</H2>
    <div className="mt-6 space-y-4">
      {[
        {
          title: "Delete my account and data",
          body: "Follow the steps on the account deletion page.",
          to: "/coacher/delete-account",
          linkText: "Account deletion",
          testId: "support-delete-link",
        },
        {
          title: "Manage or cancel a subscription",
          body: "Subscriptions are managed through your app store account settings on your device.",
          to: null,
          testId: "support-subscription-note",
        },
        {
          title: "Something isn't working",
          body: `Email ${CONTACT} with a description of the issue and we'll take a look.`,
          to: null,
          testId: "support-issue-note",
        },
      ].map((c) => (
        <div key={c.title} data-testid={c.testId} className="rounded-[20px] border border-ink/10 bg-white p-6">
          <h3 className="font-display text-base font-semibold text-ink">{c.title}</h3>
          <p className="mt-2 font-body text-sm leading-relaxed text-slate">{c.body}</p>
          {c.to && (
            <Link to={c.to} className="mt-3 inline-block font-body text-sm font-semibold text-growth underline underline-offset-4">
              {c.linkText}
            </Link>
          )}
        </div>
      ))}
    </div>
    <H2>Product</H2>
    <P>
      CoachER is a product of Build Make Win Ventures LLC. Learn more at{" "}
      <a href="https://trycoacher.com" target="_blank" rel="noopener noreferrer" data-testid="support-trycoacher-link" className="font-semibold text-growth underline underline-offset-4">
        trycoacher.com
      </a>
      .
    </P>
  </Shell>
);

export const DeleteAccountPage = () => (
  <Shell
    testId="delete-account-page"
    eyebrow="CoachER account"
    title="Delete your account"
    intro="How to request deletion of your CoachER account and associated data."
  >
    <H2>Request deletion</H2>
    <P>
      Email{" "}
      <a
        href={`mailto:${CONTACT}?subject=Delete%20my%20CoachER%20account`}
        data-testid="delete-account-email-link"
        className="font-semibold text-growth underline underline-offset-4"
      >
        {CONTACT}
      </a>{" "}
      from the email address associated with your CoachER account with the subject line
      "Delete my CoachER account". We will confirm by email once your request is complete.
    </P>
    <H2>Before you delete</H2>
    <P>
      If you have an active CoachER subscription, cancel it first in your app store account
      settings. Deleting your CoachER account does not cancel recurring billing managed by the
      app store.
    </P>
    <H2>What gets deleted</H2>
    <P>
      Owner-approved copy pending. This section will describe exactly which account, team, and
      player data is removed and what limited records may be retained for legal or billing
      purposes.
    </P>
    <H2>Questions</H2>
    <P>
      <a href={`mailto:${CONTACT}`} data-testid="delete-account-contact-email" className="font-semibold text-growth underline underline-offset-4">
        {CONTACT}
      </a>
    </P>
  </Shell>
);
